/**
 * FAQ section for service pages
 */

'use client';

import { useState } from 'react';
import type { Service, FAQ } from '@/types/data.types';

interface FAQSectionProps {
  service: Service;
  faqs?: FAQ[]; // Optional: use localized FAQs if provided, otherwise use service.faqs
}

export default function FAQSection({ service, faqs }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  
  // Use provided FAQs (localized) or fall back to service FAQs
  const displayFAQs = faqs || service.faqs;

  if (!displayFAQs || displayFAQs.length === 0) return null;

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 text-center">
            Frequently Asked{' '}
            <span className="bg-gradient-to-r from-white to-yellow bg-clip-text text-transparent">
              Questions
            </span>
          </h2>
          <p className="text-white/80 text-center mb-12">
            Common questions about our {service.name.toLowerCase()} services
          </p>
          
          <div className="space-y-4">
            {displayFAQs.map((faq, index) => (
              <div 
                key={index}
                className="bg-foreground border border-border rounded-lg overflow-hidden"
              >
                <button
                  id={`faq-${index}`}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-foreground/50 transition-colors"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-content-${index}`}
                  aria-label={`${openIndex === index ? 'Collapse' : 'Expand'} FAQ: ${faq.question}`}
                >
                  <span className="font-semibold text-white pr-4">
                    {faq.question}
                  </span>
                  <svg 
                    className={`w-5 h-5 text-yellow flex-shrink-0 transform transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {openIndex === index && (
                  <div 
                    id={`faq-content-${index}`}
                    className="px-6 pb-4 text-white/80 leading-relaxed border-t border-border pt-4"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
